import { createAxiosInstance } from '../utils/axios.js'
import { translationService } from './translation.js'

const api = createAxiosInstance()

api.interceptors.request.use(config => {
  const language = translationService.getLanguage()
  config.headers['Accept-Language'] = language
  config.headers['X-Language'] = language
  return config
})

export const publicService = {
  async getActiveDeliveryOptions () {
    const response = await api.get('/api/delivery-options/active')
    return response.data
  },
  async getCareers () {
    const response = await api.get('/api/careers')
    return response.data
  },

  async searchCareers (query) {
    const response = await api.get('/api/careers/search', {
      params: { language: query }
    })
    return response.data
  },

  async getCareer (id) {
    const response = await api.get(`/api/careers/${id}`)
    return response.data
  },

  async getEvents () {
    const response = await api.get('/api/events')
    return response.data
  },

  async searchEvents (query) {
    const response = await api.get('/api/events/search', {
      params: { language: query }
    })
    return response.data
  },

  async getEvent (id) {
    const response = await api.get(`/api/events/${id}`)
    return response.data
  },

  async getPress () {
    const response = await api.get('/api/press')
    return response.data
  },

  async getPressItem (id) {
    const response = await api.get(`/api/press/${id}`)
    return response.data
  },

  async getBanners () {
    const response = await api.get('/api/banners')
    return response.data
  },

  async getTextBanners (position = null, page = null) {
    const params = {}
    if (position) params.position = position
    if (page) params.page = page
    const response = await api.get('/api/promotional-text-banners', { params })
    return response.data
  },

  async getImageBanners (position = null, page = null) {
    const params = {}
    if (position) params.position = position
    if (page) params.page = page
    const response = await api.get('/api/banners', { params })
    return response.data
  },

  async subscribeNewsletter (email, language = 'english') {
    const response = await api.post('/api/newsletter/subscribe', {
      email,
      language
    })
    return response.data
  },

  async unsubscribeNewsletter (email) {
    const response = await api.post('/api/newsletter/unsubscribe', { email })
    return response.data
  },

  async submitManuscript (submission) {
    const response = await api.post('/api/manuscripts', submission)
    return response.data
  },

  async getPage (slug) {
    const response = await api.get(`/api/pages/${slug}`)
    return response.data
  },

  async getAllPages () {
    const response = await api.get('/api/pages')
    return response.data
  },

  async getTeamMembers () {
    const response = await api.get('/api/team-members')
    // Some endpoints return a paginated wrapper: { data: [...], pagination: {...} }
    // Normalize to return the array of members for public usage.
    if (response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    return response.data
  },

  async getTeamMember (id) {
    const response = await api.get(`/api/team-members/${id}`)
    return response.data
  },

  async getPartners () {
    const response = await api.get('/api/partners')
    return response.data
  },

  async getPartner (id) {
    const response = await api.get(`/api/partners/${id}`)
    return response.data
  },

  async getFaqs () {
    const response = await api.get('/api/faqs')
    return response.data
  },

  async getFaq (id) {
    const response = await api.get(`/api/faqs/${id}`)
    return response.data
  },

  async getBookEmptyMessages () {
    const response = await api.get('/api/book-empty-messages')
    return response.data
  },

  async getCategories (params = {}) {
    const response = await api.get('/api/categories', { params })
    return response.data
  },

  async recordPageView (pagePath) {
    try {
      const { getOrCreateSessionId, shouldRecordVisit } = await import(
        '../utils/session.js'
      )

      if (!shouldRecordVisit()) {
        return
      }

      const sessionId = getOrCreateSessionId()

      await api.post('/api/analytics', {
        event_type: 'page_view',
        page_path: pagePath,
        book_id: null,
        search_query: null,
        session_id: sessionId
      })
    } catch (error) {
      console.error('Failed to record page view:', error)
    }
  },

  async recordSearch (searchQuery, pagePath = '/books') {
    if (!searchQuery || !searchQuery.trim()) {
      return
    }
    try {
      await api.post('/api/analytics', {
        event_type: 'search',
        page_path: pagePath,
        book_id: null,
        search_query: searchQuery.trim()
      })
    } catch (error) {
      console.error('Failed to record search:', error)
    }
  }
}
